import { RumInitConfiguration } from "@datadog/browser-rum";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";
import { beforeSend } from "./scrubbing";

// Pure configuration layer for Datadog RUM. No SDK side effects here so it can
// be unit-tested directly: `getDatadogRumConfig()` returns either a ready-to-use
// init configuration or `null` when RUM should not run.

const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0; // Session Replay OFF by default (PII safety).

const parseRate = (value: string | undefined, fallback: number): number => {
  if (value === undefined || value === "") return fallback;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

// Distributed tracing makes the SDK inject `x-datadog-*` / `traceparent`
// headers into matching API requests, which turns them into CORS preflight
// requests. Until the backend allows those headers in its CORS policy, this
// MUST stay opt-in (REACT_APP_DD_ENABLE_TRACING="true") or every API call to a
// matched host is blocked by the browser. Off by default.
const getAllowedTracingUrls = (): string[] => {
  if (process.env.REACT_APP_DD_ENABLE_TRACING !== "true") return [];

  return [
    process.env.REACT_APP_API_BASE_URI,
    process.env.REACT_APP_DOCUMENT_SERVICE_URI,
    process.env.REACT_APP_ENTITLEMENT_SERVICE_URI,
    process.env.REACT_APP_REPORTING_SERVICE_URI,
  ].filter((url): url is string => Boolean(url));
};

/**
 * RUM runs only when the feature is toggled on for the current stage AND the
 * required credentials are present. Keeps RUM inert in environments that lack
 * an application id / client token.
 */
export const isDatadogRumEnabled = (): boolean =>
  FeatureToggleService.isFeatureEnabled(ToggledFeature.DatadogRum) &&
  Boolean(process.env.REACT_APP_DD_APPLICATION_ID) &&
  Boolean(process.env.REACT_APP_DD_CLIENT_TOKEN);

/**
 * Builds the Datadog RUM init configuration from environment variables, or
 * returns `null` when RUM is disabled / not configured.
 */
export const getDatadogRumConfig = (): RumInitConfiguration | null => {
  if (!isDatadogRumEnabled()) return null;

  const version = process.env.REACT_APP_DD_VERSION;
  const allowedTracingUrls = getAllowedTracingUrls();

  return {
    applicationId: process.env.REACT_APP_DD_APPLICATION_ID as string,
    clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN as string,
    site: process.env.REACT_APP_DD_SITE as RumInitConfiguration["site"],
    service: process.env.REACT_APP_DD_SERVICE || "lpc-ui",
    env: process.env.REACT_APP_STAGE,
    ...(version ? { version } : {}),
    sessionSampleRate: parseRate(process.env.REACT_APP_DD_SESSION_SAMPLE_RATE, DEFAULT_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: parseRate(
      process.env.REACT_APP_DD_SESSION_REPLAY_SAMPLE_RATE,
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE
    ),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    // Views are started manually (route-pattern names) via viewTracking; see
    // useDatadogViewTracking. Required so RUM does not also auto-create views.
    trackViewsManually: true,
    defaultPrivacyLevel: "mask",
    // Omit entirely when tracing is disabled so the SDK injects no trace
    // headers (avoids CORS preflight failures on API calls).
    ...(allowedTracingUrls.length ? { allowedTracingUrls } : {}),
    beforeSend,
  };
};
